import React, { useEffect, useMemo, useState } from "react";
import Swal from "sweetalert2";

import Sidebar from "../../Component/Sidebar/Sidebar";
import Header from "../../Component/Header/Header";
import {
  fetch as apiFetch,
  postData,
  deleteFunction,
} from "../../Component/function/FunctionApi";

const RepresentativeSettlement = () => {
  // =========================
  // Representatives
  // =========================
  const [representatives, setRepresentatives] = useState([]);
  const [representativeSearch, setRepresentativeSearch] = useState("");
  const [selectedRepresentative, setSelectedRepresentative] =
    useState(null);

  // =========================
  // Add Settlement Modal
  // =========================
  const [showAddModal, setShowAddModal] = useState(false);

  const [settlementDate, setSettlementDate] = useState(
    new Date().toISOString().slice(0, 16)
  );

  const [cashAmount, setCashAmount] = useState("");
  const [instaPayAmount, setInstaPayAmount] = useState("");
  const [vodafoneCashAmount, setVodafoneCashAmount] = useState("");
  const [notes, setNotes] = useState("");
  const [formError, setFormError] = useState("");

  // =========================
  // Settlements
  // =========================
  const [settlements, setSettlements] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // =========================
  // Payment Methods Summary
  // =========================
  const [paymentSummary, setPaymentSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);

  // =========================
  // Filters
  // =========================
  const [filterRepresentativeId, setFilterRepresentativeId] =
    useState("");
  const [filterDate, setFilterDate] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [status, setStatus] = useState("");

  // =========================
  // Details
  // =========================
  const [selectedSettlement, setSelectedSettlement] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);

  // =========================
  // Get Representatives
  // =========================
  const getRepresentatives = async () => {
    try {
      console.log("GET REPRESENTATIVES START");

      const response = await apiFetch(
        "Represntative/get-Represntatives"
      );

      console.log("REPRESENTATIVES RESPONSE:", response);

      const data = response?.data || response || [];

      setRepresentatives(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Error fetching representatives:", error);

      Swal.fire({
        icon: "error",
        title: "خطأ",
        text: "حدث خطأ أثناء جلب المندوبين",
        confirmButtonText: "حسناً",
      });
    }
  };

  // =========================
  // Get Settlements
  // =========================
  const getSettlements = async () => {
    try {
      setLoading(true);

      const params = {};

      if (filterRepresentativeId) {
        params.RepresentativeId = filterRepresentativeId;
      }

      if (filterDate) {
        params.Date = filterDate;
      }

      if (fromDate) {
        params.FromDate = fromDate;
      }

      if (toDate) {
        params.ToDate = toDate;
      }

      if (status) {
        params.Status = status;
      }

      const queryString = new URLSearchParams(params).toString();

      const response = await apiFetch(
        `RepresentativeSettlement${
          queryString ? `?${queryString}` : ""
        }`
      );

      const data = response?.data || [];

      setSettlements(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Error fetching settlements:", error);

      Swal.fire({
        icon: "error",
        title: "خطأ",
        text: "حدث خطأ أثناء جلب التسويات",
        confirmButtonText: "حسناً",
      });
    } finally {
      setLoading(false);
    }
  };

  // =========================
  // Payment Methods Summary
  // =========================
  const getPaymentSummary = async () => {
    try {
      setSummaryLoading(true);

      const params = {};

      if (filterRepresentativeId) {
        params.RepresentativeId = filterRepresentativeId;
      }

      if (filterDate) {
        params.Date = filterDate;
      }

      if (fromDate) {
        params.FromDate = fromDate;
      }

      if (toDate) {
        params.ToDate = toDate;
      }

      if (status) {
        params.Status = status;
      }

      const queryString = new URLSearchParams(params).toString();

      const response = await apiFetch(
        `RepresentativeSettlement/payment-methods-summary${
          queryString ? `?${queryString}` : ""
        }`
      );

      const data = response?.data || null;

      setPaymentSummary(data);
    } catch (error) {
      console.error(
        "Error fetching payment methods summary:",
        error
      );

      setPaymentSummary(null);
    } finally {
      setSummaryLoading(false);
    }
  };

  // =========================
  // Initial Load
  // =========================
  useEffect(() => {
    getRepresentatives();
    getSettlements();
    getPaymentSummary();
  }, []);

  // =========================
  // Search Representatives
  // =========================
  const filteredRepresentatives = useMemo(() => {
    const search = representativeSearch.trim().toLowerCase();

    if (!search) {
      return representatives;
    }

    return representatives.filter((rep) => {
      const name = String(rep.fullName || "").toLowerCase();
      const phone = String(rep.phoneNumber || "").toLowerCase();

      return (
        name.includes(search) ||
        phone.includes(search)
      );
    });
  }, [representatives, representativeSearch]);

  // =========================
  // Select Representative
  // =========================
  const handleSelectRepresentative = (rep) => {
    setSelectedRepresentative(rep);
    setRepresentativeSearch(rep.fullName || "");
  };

  // =========================
  // Open Add Modal
  // =========================
  const openAddModal = () => {
    setFormError("");
    setSelectedRepresentative(null);
    setRepresentativeSearch("");

    setCashAmount("");
    setInstaPayAmount("");
    setVodafoneCashAmount("");

    setNotes("");

    setSettlementDate(
      new Date().toISOString().slice(0, 16)
    );

    setShowAddModal(true);
  };

  // =========================
  // Reset Form
  // =========================
  const resetForm = () => {
    setSelectedRepresentative(null);
    setRepresentativeSearch("");

    setCashAmount("");
    setInstaPayAmount("");
    setVodafoneCashAmount("");

    setNotes("");
    setFormError("");

    setSettlementDate(
      new Date().toISOString().slice(0, 16)
    );
  };

  // =========================
  // Close Modal
  // =========================
  const closeAddModal = () => {
    setShowAddModal(false);
    resetForm();
  };

  // =========================
  // Total Paid
  // =========================
  const totalPaid =
    Number(cashAmount || 0) +
    Number(instaPayAmount || 0) +
    Number(vodafoneCashAmount || 0);

  // =========================
  // Create Settlement
  // =========================
  const handleCreateSettlement = async (e) => {
    e.preventDefault();

    if (!selectedRepresentative) {
      Swal.fire({
        icon: "warning",
        title: "اختر المندوب",
        text: "يجب اختيار المندوب أولاً",
        confirmButtonText: "حسناً",
      });

      return;
    }

    if (totalPaid <= 0) {
      Swal.fire({
        icon: "warning",
        title: "المبلغ غير صحيح",
        text: "من فضلك أدخل مبلغ في وسيلة دفع واحدة على الأقل",
        confirmButtonText: "حسناً",
      });

      return;
    }

    try {
      setSaving(true);
      setFormError("");

      const payload = {
        representativeId: selectedRepresentative.id,

        settlementDate: new Date(
          settlementDate
        ).toISOString(),

        cashAmount: Number(cashAmount || 0),

        instaPayAmount: Number(
          instaPayAmount || 0
        ),

        vodafoneCashAmount: Number(
          vodafoneCashAmount || 0
        ),

        paidAmount: totalPaid,

        notes: notes.trim(),
      };

      console.log(
        "CREATE SETTLEMENT PAYLOAD:",
        payload
      );

      await postData(
        "RepresentativeSettlement",
        payload,
        setFormError
      );

      closeAddModal();

      await Swal.fire({
        icon: "success",
        title: "تم الحفظ",
        text: "تم إنشاء تسوية المندوب بنجاح",
        confirmButtonText: "حسناً",
      });

      await getSettlements();
      await getPaymentSummary();
    } catch (error) {
      console.error(
        "Error creating settlement:",
        error
      );

      Swal.fire({
        icon: "error",
        title: "فشل الحفظ",
        text:
          error?.response?.data?.message ||
          "حدث خطأ أثناء إنشاء التسوية",
        confirmButtonText: "حسناً",
      });
    } finally {
      setSaving(false);
    }
  };

  // =========================
  // Settlement Details
  // =========================
  const handleViewDetails = async (id) => {
    try {
      setDetailsLoading(true);

      const response = await apiFetch(
        `RepresentativeSettlement/${id}`
      );

      const data = response?.data || response;

      setSelectedSettlement(data);
    } catch (error) {
      console.error(
        "Error fetching settlement details:",
        error
      );

      Swal.fire({
        icon: "error",
        title: "خطأ",
        text: "تعذر جلب تفاصيل التسوية",
        confirmButtonText: "حسناً",
      });
    } finally {
      setDetailsLoading(false);
    }
  };

  // =========================
  // Delete Settlement
  // =========================
  const handleDelete = async (id) => {
    const result = await Swal.fire({
      icon: "warning",
      title: "هل أنت متأكد؟",
      text: "سيتم حذف تسوية المندوب ولا يمكن التراجع عن العملية",
      showCancelButton: true,
      confirmButtonText: "نعم، احذف",
      cancelButtonText: "إلغاء",
      reverseButtons: true,
    });

    if (!result.isConfirmed) {
      return;
    }

    try {
      await deleteFunction(
        `RepresentativeSettlement/${id}`
      );

      await Swal.fire({
        icon: "success",
        title: "تم الحذف",
        text: "تم حذف التسوية بنجاح",
        confirmButtonText: "حسناً",
      });

      await getSettlements();
      await getPaymentSummary();
    } catch (error) {
      console.error(
        "Error deleting settlement:",
        error
      );

      Swal.fire({
        icon: "error",
        title: "فشل الحذف",
        text:
          error?.response?.data?.message ||
          "حدث خطأ أثناء حذف التسوية",
        confirmButtonText: "حسناً",
      });
    }
  };

  // =========================
  // Search
  // =========================
  const handleSearch = async () => {
    await getSettlements();
    await getPaymentSummary();
  };

  // =========================
  // Clear Filters
  // =========================
  const clearFilters = async () => {
    setFilterRepresentativeId("");
    setFilterDate("");
    setFromDate("");
    setToDate("");
    setStatus("");

    setTimeout(async () => {
      await getSettlements();
      await getPaymentSummary();
    }, 0);
  };

  // =========================
  // Helpers
  // =========================
  const getRepresentativeName = (rep) => {
    return rep?.fullName || "غير معروف";
  };

  const getRepresentativePhone = (rep) => {
    return rep?.phoneNumber || "-";
  };

  const formatMoney = (value) => {
    return `${Number(value || 0).toLocaleString(
      "ar-EG"
    )} ج.م`;
  };

  const formatDate = (date) => {
    if (!date) return "-";

    return new Date(date).toLocaleDateString(
      "ar-EG"
    );
  };

  return (
    <div className="apDiv ChatApp min-h-screen bg-slate-50">
      <Sidebar />

      <div
        className="body_container container"
        dir="rtl"
      >
        <div className="Header">
          <Header />
        </div>

        <main className="px-4 py-6 md:px-6 lg:px-8">

          {/* =========================
              Page Header
          ========================= */}
          <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
            <div>
              <h2 className="text-2xl font-bold text-slate-800">
                تسويات المندوبين
              </h2>

              <p className="mt-1 text-sm text-slate-500">
                إدارة وتسوية حسابات المندوبين
              </p>
            </div>

            <button
              type="button"
              onClick={openAddModal}
              className="flex items-center gap-2 rounded-xl bg-blue-600 px-6 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-blue-700"
            >
              <i className="fa-light fa-plus" />
              إضافة تسوية جديدة
            </button>
          </div>

          {/* =========================
              Payment Summary
          ========================= */}
          <section className="mb-6">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">

              <SummaryCard
                title="إجمالي الكاش"
                value={
                  summaryLoading
                    ? "..."
                    : formatMoney(
                        paymentSummary?.totalCashAmount
                      )
                }
                icon="fa-money-bill-wave"
                className="bg-emerald-50 text-emerald-600"
              />

              <SummaryCard
                title="إجمالي InstaPay"
                value={
                  summaryLoading
                    ? "..."
                    : formatMoney(
                        paymentSummary?.totalInstaPayAmount
                      )
                }
                icon="fa-mobile-screen-button"
                className="bg-blue-50 text-blue-600"
              />

              <SummaryCard
                title="إجمالي Vodafone Cash"
                value={
                  summaryLoading
                    ? "..."
                    : formatMoney(
                        paymentSummary?.totalVodafoneCashAmount
                      )
                }
                icon="fa-wallet"
                className="bg-red-50 text-red-600"
              />

              <SummaryCard
                title="إجمالي المدفوع"
                value={
                  summaryLoading
                    ? "..."
                    : formatMoney(
                        paymentSummary?.totalOverallPaidAmount
                      )
                }
                icon="fa-coins"
                className="bg-amber-50 text-amber-600"
              />

            </div>
          </section>

          {/* =========================
              Filters
          ========================= */}
          <section className="mb-6 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <div className="mb-5 flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-slate-100 text-slate-600">
                <i className="fa-light fa-filter" />
              </div>

              <div>
                <h3 className="text-lg font-bold text-slate-800">
                  فلترة التسويات
                </h3>

                <p className="text-sm text-slate-500">
                  ابحث في سجلات التسويات
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-5">

              <select
                value={filterRepresentativeId}
                onChange={(e) =>
                  setFilterRepresentativeId(
                    e.target.value
                  )
                }
                className="rounded-xl border border-slate-300 bg-white px-3 py-3 text-sm outline-none focus:border-blue-500"
              >
                <option value="">
                  كل المندوبين
                </option>

                {representatives.map((rep) => (
                  <option
                    key={rep.id}
                    value={rep.id}
                  >
                    {getRepresentativeName(rep)}
                  </option>
                ))}
              </select>

              <input
                type="date"
                value={filterDate}
                onChange={(e) =>
                  setFilterDate(e.target.value)
                }
                className="rounded-xl border border-slate-300 px-3 py-3 text-sm outline-none focus:border-blue-500"
              />

              <input
                type="date"
                value={fromDate}
                onChange={(e) =>
                  setFromDate(e.target.value)
                }
                className="rounded-xl border border-slate-300 px-3 py-3 text-sm outline-none focus:border-blue-500"
              />

              <input
                type="date"
                value={toDate}
                onChange={(e) =>
                  setToDate(e.target.value)
                }
                className="rounded-xl border border-slate-300 px-3 py-3 text-sm outline-none focus:border-blue-500"
              />

              <select
                value={status}
                onChange={(e) =>
                  setStatus(e.target.value)
                }
                className="rounded-xl border border-slate-300 bg-white px-3 py-3 text-sm outline-none focus:border-blue-500"
              >
                <option value="">
                  كل الحالات
                </option>

                <option value="تقفيل كامل">
                  تقفيل كامل
                </option>

                <option value="تقفيل جزئي">
                  تقفيل جزئي
                </option>

                <option value="غير مسوى">
                  غير مسوى
                </option>
              </select>
            </div>

            <div className="mt-4 flex flex-wrap gap-3">

              <button
                type="button"
                onClick={handleSearch}
                className="rounded-xl bg-blue-600 px-6 py-3 text-sm font-semibold text-white transition hover:bg-blue-700"
              >
                <i className="fa-light fa-magnifying-glass ml-2" />
                بحث
              </button>

              <button
                type="button"
                onClick={clearFilters}
                className="rounded-xl border border-slate-300 bg-white px-6 py-3 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
              >
                <i className="fa-light fa-rotate-right ml-2" />
                إعادة ضبط
              </button>

            </div>
          </section>

          {/* =========================
              Table
          ========================= */}
          <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">

            <div className="flex items-center justify-between border-b border-slate-200 p-5">
              <div>
                <h3 className="text-lg font-bold text-slate-800">
                  سجل التسويات
                </h3>

                <p className="text-sm text-slate-500">
                  إجمالي السجلات: {settlements.length}
                </p>
              </div>

              <button
                type="button"
                onClick={handleSearch}
                className="flex h-10 w-10 items-center justify-center rounded-xl bg-slate-100 text-slate-600 transition hover:bg-blue-50 hover:text-blue-600"
              >
                <i className="fa-light fa-arrows-rotate" />
              </button>
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-[1800px] w-full text-right text-sm">

                <thead className="bg-slate-50 text-slate-600">
                  <tr>
                    <th className="px-4 py-4">#</th>
                    <th className="px-4 py-4">المندوب</th>
                    <th className="px-4 py-4">التاريخ</th>
                    <th className="px-4 py-4">
                      إجمالي الفواتير
                    </th>
                    <th className="px-4 py-4">
                      المبلغ المسلم
                    </th>
                    <th className="px-4 py-4">
                      التسليم
                    </th>
                    <th className="px-4 py-4">
                      المرتجع
                    </th>
                    <th className="px-4 py-4">
                      المعلق
                    </th>
                    <th className="px-4 py-4">
                      الشحن
                    </th>
                    <th className="px-4 py-4">
                      المطلوب
                    </th>

                    {/* NEW */}
                    <th className="px-4 py-4">
                      كاش
                    </th>

                    <th className="px-4 py-4">
                      InstaPay
                    </th>

                    <th className="px-4 py-4">
                      Vodafone Cash
                    </th>

                    <th className="px-4 py-4">
                      المدفوع
                    </th>

                    <th className="px-4 py-4">
                      المتبقي
                    </th>

                    <th className="px-4 py-4">
                      الحالة
                    </th>

                    <th className="px-4 py-4">
                      الإجراءات
                    </th>
                  </tr>
                </thead>

                <tbody className="divide-y divide-slate-100">

                  {loading ? (
                    <tr>
                      <td
                        colSpan="17"
                        className="px-4 py-12 text-center text-slate-500"
                      >
                        <i className="fa-light fa-spinner fa-spin ml-2" />
                        جاري تحميل التسويات...
                      </td>
                    </tr>
                  ) : settlements.length === 0 ? (
                    <tr>
                      <td
                        colSpan="17"
                        className="px-4 py-12 text-center text-slate-500"
                      >
                        لا توجد تسويات
                      </td>
                    </tr>
                  ) : (
                    settlements.map((item) => (
                      <tr
                        key={item.id}
                        className="transition hover:bg-slate-50"
                      >

                        <td className="px-4 py-4 font-semibold">
                          {item.id}
                        </td>

                        <td className="px-4 py-4">
                          <div className="font-semibold text-slate-800">
                            {item.representativeName}
                          </div>

                          <div className="text-xs text-slate-400">
                            #{item.representativeId}
                          </div>
                        </td>

                        <td className="px-4 py-4">
                          {formatDate(
                            item.settlementDate
                          )}
                        </td>

                        <td className="px-4 py-4">
                          {item.totalHandedInvoicesCount}
                        </td>

                        <td className="px-4 py-4 font-semibold">
                          {formatMoney(
                            item.totalHandedAmount
                          )}
                        </td>

                        <td className="px-4 py-4">
                          <div>
                            {item.deliveredInvoicesCount}
                          </div>

                          <span className="text-xs text-green-600">
                            {formatMoney(
                              item.deliveredAmount
                            )}
                          </span>
                        </td>

                        <td className="px-4 py-4">
                          <div>
                            {item.returnedInvoicesCount}
                          </div>

                          <span className="text-xs text-red-600">
                            {formatMoney(
                              item.returnedAmount
                            )}
                          </span>
                        </td>

                        <td className="px-4 py-4">
                          <div>
                            {item.pendingInvoicesCount}
                          </div>

                          <span className="text-xs text-orange-600">
                            {formatMoney(
                              item.pendingAmount
                            )}
                          </span>
                        </td>

                        <td className="px-4 py-4">
                          {formatMoney(
                            item.shippingAmount
                          )}
                        </td>

                        <td className="px-4 py-4 font-bold text-slate-800">
                          {formatMoney(
                            item.netRequiredAmount
                          )}
                        </td>

                        {/* Cash */}
                        <td className="px-4 py-4 font-semibold text-emerald-600">
                          {formatMoney(
                            item.cashAmount
                          )}
                        </td>

                        {/* InstaPay */}
                        <td className="px-4 py-4 font-semibold text-blue-600">
                          {formatMoney(
                            item.instaPayAmount
                          )}
                        </td>

                        {/* Vodafone Cash */}
                        <td className="px-4 py-4 font-semibold text-red-600">
                          {formatMoney(
                            item.vodafoneCashAmount
                          )}
                        </td>

                        {/* Paid */}
                        <td className="px-4 py-4 font-bold text-blue-600">
                          {formatMoney(
                            item.paidAmount
                          )}
                        </td>

                        <td className="px-4 py-4">
                          <span
                            className={`font-bold ${
                              Number(
                                item.remainingAmount
                              ) < 0
                                ? "text-red-600"
                                : Number(
                                    item.remainingAmount
                                  ) > 0
                                ? "text-orange-600"
                                : "text-green-600"
                            }`}
                          >
                            {formatMoney(
                              Math.abs(
                                item.remainingAmount
                              )
                            )}
                          </span>
                        </td>

                        <td className="px-4 py-4">
                          <span
                            className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${
                              item.status ===
                              "تقفيل كامل"
                                ? "bg-green-100 text-green-700"
                                : item.status ===
                                  "تقفيل جزئي"
                                ? "bg-orange-100 text-orange-700"
                                : "bg-slate-100 text-slate-700"
                            }`}
                          >
                            {item.status}
                          </span>
                        </td>

                        <td className="px-4 py-4">
                          <div className="flex items-center gap-2">

                            <button
                              type="button"
                              onClick={() =>
                                handleViewDetails(
                                  item.id
                                )
                              }
                              className="flex h-9 w-9 items-center justify-center rounded-lg bg-blue-50 text-blue-600 transition hover:bg-blue-100"
                              title="التفاصيل"
                            >
                              <i className="fa-light fa-eye" />
                            </button>

                            <button
                              type="button"
                              onClick={() =>
                                handleDelete(item.id)
                              }
                              className="flex h-9 w-9 items-center justify-center rounded-lg bg-red-50 text-red-600 transition hover:bg-red-100"
                              title="حذف"
                            >
                              <i className="fa-light fa-trash" />
                            </button>

                          </div>
                        </td>

                      </tr>
                    ))
                  )}

                </tbody>
              </table>
            </div>
          </section>
        </main>
      </div>

      {/* =====================================================
          ADD SETTLEMENT MODAL
      ===================================================== */}
      {showAddModal && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 p-4">
          <div
            className="max-h-[90vh] w-full max-w-3xl overflow-y-auto rounded-2xl bg-white shadow-2xl"
            dir="rtl"
          >

            {/* Header */}
            <div className="flex items-center justify-between border-b border-slate-200 p-5">

              <div className="flex items-center gap-3">

                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-blue-50 text-blue-600">
                  <i className="fa-light fa-file-invoice-dollar" />
                </div>

                <div>
                  <h3 className="text-lg font-bold text-slate-800">
                    إضافة تسوية جديدة
                  </h3>

                  <p className="text-sm text-slate-500">
                    تسجيل المبالغ المدفوعة حسب وسيلة الدفع
                  </p>
                </div>

              </div>

              <button
                type="button"
                onClick={closeAddModal}
                className="flex h-10 w-10 items-center justify-center rounded-xl bg-slate-100 text-slate-600 hover:bg-red-50 hover:text-red-600"
              >
                <i className="fa-light fa-xmark text-lg" />
              </button>

            </div>

            {/* Form */}
            <form
              onSubmit={handleCreateSettlement}
              className="p-5"
            >
              <div className="grid grid-cols-1 gap-5 md:grid-cols-2">

                {/* Representative */}
                <div className="relative md:col-span-2">

                  <label className="mb-2 block text-sm font-semibold text-slate-700">
                    المندوب
                  </label>

                  <input
                    type="text"
                    value={representativeSearch}
                    onChange={(e) => {
                      setRepresentativeSearch(
                        e.target.value
                      );
                      setSelectedRepresentative(null);
                    }}
                    placeholder="ابحث بالاسم أو رقم الهاتف..."
                    className="w-full rounded-xl border border-slate-300 bg-white px-4 py-3 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                  />

                  {representativeSearch &&
                    !selectedRepresentative &&
                    filteredRepresentatives.length > 0 && (
                      <div className="absolute right-0 left-0 top-[78px] z-50 max-h-60 overflow-y-auto rounded-xl border border-slate-200 bg-white shadow-xl">

                        {filteredRepresentatives.map(
                          (rep) => (
                            <button
                              type="button"
                              key={rep.id}
                              onClick={() =>
                                handleSelectRepresentative(
                                  rep
                                )
                              }
                              className="flex w-full items-center justify-between border-b border-slate-100 px-4 py-3 text-right transition hover:bg-blue-50"
                            >

                              <div>
                                <div className="font-semibold text-slate-800">
                                  {rep.fullName}
                                </div>

                                <div className="text-xs text-slate-500">
                                  {rep.phoneNumber}
                                </div>
                              </div>

                              <i className="fa-light fa-user text-blue-500" />

                            </button>
                          )
                        )}

                      </div>
                    )}

                  {selectedRepresentative && (
                    <div className="mt-2 flex items-center justify-between rounded-xl bg-blue-50 px-4 py-3">

                      <div>
                        <div className="font-bold text-blue-800">
                          {selectedRepresentative.fullName}
                        </div>

                        <div className="text-xs text-blue-600">
                          {selectedRepresentative.phoneNumber}
                        </div>
                      </div>

                      <button
                        type="button"
                        onClick={() => {
                          setSelectedRepresentative(null);
                          setRepresentativeSearch("");
                        }}
                        className="text-red-500 hover:text-red-700"
                      >
                        <i className="fa-light fa-xmark" />
                      </button>

                    </div>
                  )}
                </div>

                {/* Date */}
                <div>
                  <label className="mb-2 block text-sm font-semibold text-slate-700">
                    تاريخ التسوية
                  </label>

                  <input
                    type="datetime-local"
                    value={settlementDate}
                    onChange={(e) =>
                      setSettlementDate(
                        e.target.value
                      )
                    }
                    className="w-full rounded-xl border border-slate-300 px-4 py-3 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                  />
                </div>

                {/* Total Paid */}
                <div>
                  <label className="mb-2 block text-sm font-semibold text-slate-700">
                    إجمالي المدفوع
                  </label>

                  <div className="flex h-[46px] items-center rounded-xl border border-blue-200 bg-blue-50 px-4 font-bold text-blue-700">
                    {formatMoney(totalPaid)}
                  </div>
                </div>

                {/* Cash */}
                <div>
                  <label className="mb-2 block text-sm font-semibold text-slate-700">
                    المبلغ كاش
                  </label>

                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={cashAmount}
                    onChange={(e) =>
                      setCashAmount(e.target.value)
                    }
                    placeholder="0"
                    className="w-full rounded-xl border border-slate-300 px-4 py-3 text-sm outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100"
                  />
                </div>

                {/* InstaPay */}
                <div>
                  <label className="mb-2 block text-sm font-semibold text-slate-700">
                    المبلغ InstaPay
                  </label>

                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={instaPayAmount}
                    onChange={(e) =>
                      setInstaPayAmount(
                        e.target.value
                      )
                    }
                    placeholder="0"
                    className="w-full rounded-xl border border-slate-300 px-4 py-3 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                  />
                </div>

                {/* Vodafone Cash */}
                <div>
                  <label className="mb-2 block text-sm font-semibold text-slate-700">
                    المبلغ Vodafone Cash
                  </label>

                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={vodafoneCashAmount}
                    onChange={(e) =>
                      setVodafoneCashAmount(
                        e.target.value
                      )
                    }
                    placeholder="0"
                    className="w-full rounded-xl border border-slate-300 px-4 py-3 text-sm outline-none focus:border-red-500 focus:ring-2 focus:ring-red-100"
                  />
                </div>

                {/* Notes */}
                <div className="md:col-span-2">
                  <label className="mb-2 block text-sm font-semibold text-slate-700">
                    ملاحظات
                  </label>

                  <textarea
                    rows="3"
                    value={notes}
                    onChange={(e) =>
                      setNotes(e.target.value)
                    }
                    placeholder="اكتب أي ملاحظات..."
                    className="w-full resize-none rounded-xl border border-slate-300 px-4 py-3 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                  />
                </div>

              </div>

              {formError && (
                <div className="mt-4 rounded-xl bg-red-50 px-4 py-3 text-sm text-red-600">
                  {formError}
                </div>
              )}

              {/* Buttons */}
              <div className="mt-5 flex justify-start gap-3 border-t border-slate-100 pt-5">

                <button
                  type="submit"
                  disabled={saving}
                  className="rounded-xl bg-blue-600 px-7 py-3 font-semibold text-white shadow-sm transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {saving ? (
                    <>
                      <i className="fa-light fa-spinner fa-spin ml-2" />
                      جاري الحفظ...
                    </>
                  ) : (
                    <>
                      <i className="fa-light fa-check ml-2" />
                      حفظ التسوية
                    </>
                  )}
                </button>

                <button
                  type="button"
                  onClick={closeAddModal}
                  className="rounded-xl border border-slate-300 bg-white px-7 py-3 font-semibold text-slate-700 hover:bg-slate-50"
                >
                  إلغاء
                </button>

              </div>
            </form>
          </div>
        </div>
      )}

      {/* =====================================================
          DETAILS MODAL
      ===================================================== */}
      {selectedSettlement && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 p-4">
          <div
            className="max-h-[90vh] w-full max-w-5xl overflow-y-auto rounded-2xl bg-white shadow-2xl"
            dir="rtl"
          >

            <div className="flex items-center justify-between border-b border-slate-200 p-5">

              <div>
                <h3 className="text-xl font-bold text-slate-800">
                  تفاصيل التسوية
                </h3>

                <p className="text-sm text-slate-500">
                  تسوية رقم #{selectedSettlement.id}
                </p>
              </div>

              <button
                type="button"
                onClick={() =>
                  setSelectedSettlement(null)
                }
                className="flex h-10 w-10 items-center justify-center rounded-xl bg-slate-100 text-slate-600 hover:bg-red-50 hover:text-red-600"
              >
                <i className="fa-light fa-xmark text-lg" />
              </button>

            </div>

            <div className="grid grid-cols-1 gap-4 p-5 sm:grid-cols-2 lg:grid-cols-3">

              <DetailCard
                title="المندوب"
                value={
                  selectedSettlement.representativeName
                }
              />

              <DetailCard
                title="تاريخ التسوية"
                value={formatDate(
                  selectedSettlement.settlementDate
                )}
              />

              <DetailCard
                title="إجمالي الفواتير"
                value={
                  selectedSettlement.totalHandedInvoicesCount
                }
              />

              <DetailCard
                title="المبلغ المسلم"
                value={formatMoney(
                  selectedSettlement.totalHandedAmount
                )}
              />

              <DetailCard
                title="المبلغ المطلوب"
                value={formatMoney(
                  selectedSettlement.netRequiredAmount
                )}
              />

              <DetailCard
                title="كاش"
                value={formatMoney(
                  selectedSettlement.cashAmount
                )}
              />

              <DetailCard
                title="InstaPay"
                value={formatMoney(
                  selectedSettlement.instaPayAmount
                )}
              />

              <DetailCard
                title="Vodafone Cash"
                value={formatMoney(
                  selectedSettlement.vodafoneCashAmount
                )}
              />

              <DetailCard
                title="إجمالي المدفوع"
                value={formatMoney(
                  selectedSettlement.paidAmount
                )}
              />

              <DetailCard
                title="المبلغ المتبقي"
                value={formatMoney(
                  Math.abs(
                    selectedSettlement.remainingAmount
                  )
                )}
              />

              <DetailCard
                title="الشحن"
                value={formatMoney(
                  selectedSettlement.shippingAmount
                )}
              />

              <DetailCard
                title="الحالة"
                value={selectedSettlement.status}
              />

              <DetailCard
                title="تاريخ الإنشاء"
                value={
                  selectedSettlement.dateCreated
                    ? new Date(
                        selectedSettlement.dateCreated
                      ).toLocaleString("ar-EG")
                    : "-"
                }
              />

              <DetailCard
                title="أنشئ بواسطة"
                value={
                  selectedSettlement.createdBy || "-"
                }
              />

            </div>

            {selectedSettlement.notes && (
              <div className="mx-5 mb-5 rounded-xl bg-slate-50 p-4">
                <div className="mb-1 text-sm font-semibold text-slate-600">
                  الملاحظات
                </div>

                <div className="text-sm text-slate-800">
                  {selectedSettlement.notes}
                </div>
              </div>
            )}

          </div>
        </div>
      )}

      {/* Details Loading */}
      {detailsLoading && (
        <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/20">
          <div className="rounded-xl bg-white px-6 py-4 shadow-xl">
            <i className="fa-light fa-spinner fa-spin ml-2" />
            جاري تحميل التفاصيل...
          </div>
        </div>
      )}
    </div>
  );
};

// =========================
// Summary Card
// =========================
const SummaryCard = ({
  title,
  value,
  icon,
  className,
}) => {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="flex items-center justify-between">

        <div>
          <p className="text-sm text-slate-500">
            {title}
          </p>

          <p className="mt-2 text-xl font-bold text-slate-800">
            {value}
          </p>
        </div>

        <div
          className={`flex h-12 w-12 items-center justify-center rounded-xl ${className}`}
        >
          <i className={`fa-light ${icon} text-xl`} />
        </div>

      </div>
    </div>
  );
};

// =========================
// Detail Card
// =========================
const DetailCard = ({ title, value }) => {
  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
      <div className="mb-1 text-xs font-medium text-slate-500">
        {title}
      </div>

      <div className="font-bold text-slate-800">
        {value ?? "-"}
      </div>
    </div>
  );
};

export default RepresentativeSettlement;
